import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FormFields } from '~/components/FormFields'
import FileUpload from '~/components/FileUpload'
import { Control, FieldValues, UseFormGetValues, UseFormSetValue, useWatch } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { b64toBlob, handleErrors } from '~/utils/helpers'
import Dialog from '~/components/Dialog'
import { Icon, Segment } from 'semantic-ui-react'
import { StyledDeleteButton, StyledFileLabel, StyledFileWrapper } from '../../MilkFixedPrice/style'
import CO2Service from '~/api/services/co2'

interface Props {
  index: number
  control: Control
  setValue: UseFormSetValue<FieldValues>
  getValues: UseFormGetValues<FieldValues>
  language: any
  languages: any[]
}

const LanguageTab: React.FC<Props> = (props) => {
  const { t } = useTranslation()
  const { id } = useParams()

  const [isBusy, setIsBusy] = useState(false)
  const [fileSource, setFileSource] = useState(null)
  const [openDeleteFileModal, setOpenFileDeleteModal] = useState(false)

  const { index, control, getValues, setValue, languages, language } = props
  const indexMapped = languages.findIndex(languageItem => languageItem.id === language.id)
  const indexValue = index || indexMapped

  const watchFile = useWatch({ control: control, name: `languages[${index}].file` })
  const watchFileName = useWatch({ control: control, name: `languages[${index}].originalFilename` })
  const watchFilename = useWatch({ control: control, name: `languages[${index}].filename` })
  const watchDescription = useWatch({ control: control, name: `languages[${index}].description` })

  async function handleDownloadPdf() {
    setIsBusy(true);
    try {
      if (watchFile) {
        setFileSource(watchFile)
      } else {
        const fileName = getValues(`languages[${index}].filename`)
        const response = await CO2Service.getCo2File(id, { fileName: fileName });
        const type = response.data.split(';')[0].split('data:')[1]
        const blob = b64toBlob(response.data, type);
        const blobUrl = URL.createObjectURL(blob);
        setFileSource(blobUrl);
      }
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false);
  }

  function renderFileField() {
    return <FormFields.External
      control={control}
      name={`languages[${index}].file`}
      label={t('File')}
      onChange={(value, name) => {
        setValue(`languages[${index}].file`, value);
        setValue(`languages[${index}].originalFilename`, name);
      }}
    >
      <FileUpload button
        encodeFiles
        accept={{
          'application/pdf': ['.pdf']
        }}
        sizeLimitInMB={10}
      />
    </FormFields.External>
  }

  function renderModalPreviewFile() {
    return <Dialog
      open={Boolean(fileSource)}
      handleClose={() => setFileSource(null)}
      textContent={<iframe width='100%' height='100%' src={fileSource}></iframe>}
      textHeader={t('Preview')}
      textClose={t('Close')}
      type={'preview'}
    />
  }

  function removeFile(index) {
    if (id) {
      setOpenFileDeleteModal(true)
    } else {
      setValue(`languages[${index}].file`, null)
      setValue(`languages[${index}].originalFilename`, null)
    }
  }

  function renderFile() {
    return <StyledFileWrapper>
      <StyledFileLabel
        basic
        color='blue'
        onClick={() => handleDownloadPdf()}
      >
        <Icon name='file' />
        <div className='fileName'>{watchFileName}</div>
      </StyledFileLabel>

      <StyledDeleteButton
        onClick={() => removeFile(index)}
        rightAlign={10}
        size={'small'}
        basic
        color='red'
        type={'button'}
        icon='trash' />
    </StyledFileWrapper>
  }

  async function handleSubmit() {
    setIsBusy(true);
    try {
      await CO2Service.deleteCo2File(id, { fileName: watchFilename })
      setValue(`languages[${index}].filename`, null)
      setValue(`languages[${index}].originalFilename`, null)
      setOpenFileDeleteModal(false)
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false);
  }

  function renderDeleteFileModal() {
    return <Dialog
      textHeader={t('Delete file')}
      textContent={t('Do you want to delete {{watchFileName}}?', { watchFileName })}
      open={openDeleteFileModal}
      handleClose={() => setOpenFileDeleteModal(false)}
      textClose={t('No')}
      textSubmit={t('Yes')}
      isLoading={isBusy}
      type={'abort'}
      handleSubmit={() => handleSubmit()}
    />
  }

  return <div>
    {renderDeleteFileModal()}
    <FormFields.Input
      control={control}
      name={`languages[${indexValue}].title`}
      label={t('Title/Topic')}
      placeholder={t('Title/Topic')}
      rules={{
        required: {
          value: true,
          message: t('Please enter title')
        },
        maxLength: {
          value: 100,
          message: t('Max char. is 100')
        }
      }}
    />
    <FormFields.TextArea
      control={control}
      name={`languages[${indexValue}].description`}
      label={t('General information about Climate Check data collection')}
      style={{ marginBottom: 0 }}
      placeholder={t('General information')}
      rules={{
        maxLength: {
          value: 1000,
          message: t('Max char. is 1000')
        }
      }}
    />
    <small>{t('Characters: {{count}} (max 1000)', { count: watchDescription?.length || 0 })}</small>
    {fileSource && renderModalPreviewFile()}
    <Segment loading={isBusy} style={{ position: 'relative', minWidth: 430 }}>
      {watchFileName && renderFile()}
      {renderFileField()}
    </Segment>
  </div>
}
export default LanguageTab